import Mustache from 'mustache';
import HedinAccordion from '../car-search/2022/hedin-accordion';

class StoreCollection {

  /**
   * Elements member variable
   * Contains elements in object
   *
   * @type {object}
   * @private
   */
  #elements = {};

  /**
   * Templates for Mustache rendering goes here
   *
   * @type {object}
   * @private
   */
  #templates = {}

  /**
   * Constructor of Stores
   *
   * @param {HTMLElement} storesPageContainerElement - Main element
   */
  constructor(storesPageContainerElement) {
    this.#elements.main = storesPageContainerElement;
    this.#getElements();
    this.#bindEvents();
  }

  /**
   * Setter for loading
   *
   * @param {boolean} value - True: loading, false: not loading. Simple as that
   */
  set isLoading(value) {
    [ this.#elements.storesListContainer, this.#elements.phoneNumbersContainer ].forEach(element => {
      element.classList.toggle('is-loading', value);
    });
  }

  /**
   * Get needed elements from dom to this.#elements
   *
   * @private
   */
  #getElements() {
    this.#elements.showFilters = document.getElementById('filter-stores-button');
    this.#elements.filtersContainer = document.getElementById('stores-filters-container');
    this.#elements.closeFiltersMobile = this.#elements.filtersContainer.querySelector('.stores-page__close-filters-mobile');
    this.#elements.showResultsButton = document.getElementById('stores-mobile-filters-show-results');
    this.#elements.resetFilters = document.getElementById('stores-mobile-filters-reset');
    this.#elements.cityCheckboxes = this.#elements.filtersContainer.querySelectorAll('.stores-cities-checkboxes input[type="checkbox"]');
    this.#elements.storesListContainer = this.#elements.main.querySelector('.stores-list-container');
    this.#elements.storesList = this.#elements.storesListContainer.querySelector('.stores-list');
    this.#elements.storesListCount = this.#elements.storesListContainer.querySelector('.stores-list-store-count');
    this.#elements.storesListHeading = this.#elements.storesListContainer.querySelector('.stores-list-heading');
    this.#elements.phoneNumbersContainer = this.#elements.main.querySelector('.phone-numbers-container');
    this.#elements.phoneNumbersList = this.#elements.phoneNumbersContainer.querySelector('.stores-page__phone-numbers');
    this.#elements.phoneNumbersHeading = this.#elements.phoneNumbersContainer.querySelector('.phone-numbers-heading');
    this.#elements.seoTextContainer = this.#elements.main.querySelector('.seo-text-element');
    this.#elements.seoText = this.#elements.main.querySelector('.seo-text-element__text');
    this.#elements.mobileFiltersContent = document.querySelector('.stores-page__filters-mobile .content');
    this.#elements.bottomButtons = document.querySelector('.stores-page__bottom-controls');

    this.#templates.storeListTemplate = document.getElementById('store-list-template').innerHTML;
    this.#templates.phoneNumbersTemplate = document.getElementById('phone-numbers-template').innerHTML;
    this.#templates.accordionTemplate = document.getElementById('accordion-template').innerHTML;
  }

  /**
   * Bind needed event listeners
   *
   * @private
   */
  #bindEvents() {
    [ this.#elements.showFilters,
      this.#elements.closeFiltersMobile,
      this.#elements.showResultsButton ].forEach(e => {
      e.addEventListener('click', () => {
        this.#elements.filtersContainer.classList.toggle('stores-page__filters--in');
        this.#elements.bottomButtons.classList.toggle('show');
      });
    });

    // Checkbox event listeners
    for(const cityChecbkox of this.#elements.cityCheckboxes) {
      cityChecbkox.addEventListener('change', this.#doSearch.bind(this));
    }

    [ window.multiselects['stores-brands-dropdown'], window.multiselects['stores-service-dropdown'] ].forEach(dropdown => {
      dropdown.addCallback({
        onValueChange: () => this.#doSearch()
      });
    });

    this.#elements.resetFilters.addEventListener('click', this.#resetFilters.bind(this));

  }

  /**
   * Executes search to stage-ajax
   */
  async #doSearch() {
    this.isLoading = true;
    const queryStringUrl = this.#generateUrl();
    history.pushState({}, '', queryStringUrl);

    const fetchData = await fetch(`/stage-ajax/hedin-location-page/${ queryStringUrl }`)
    const jsonData = await fetchData.json();

    this.isLoading = false;

    this.#elements.storesListCount.innerText = jsonData.locationsCount;

    const storesListMarkup = jsonData.locations.map(l => Mustache.render(this.#templates.storeListTemplate, l)).join('');
    this.#elements.storesList.innerHTML = storesListMarkup;
    this.#elements.storesListHeading.innerHTML = jsonData.headings.storesListHeading;

    if(jsonData.phoneNumbers.length) {
      this.#elements.phoneNumbersList.innerHTML = Mustache.render(this.#templates.phoneNumbersTemplate, { phoneNumbers: jsonData.phoneNumbers });
      this.#elements.phoneNumbersHeading.innerHTML = jsonData.headings.phoneNumbersHeading;
    }
    this.#elements.phoneNumbersContainer.classList.toggle('hidden', !jsonData.phoneNumbers.length);

    if(jsonData.seoText?.faq) {
      this.#elements.seoText.innerHTML = Mustache.render(this.#templates.accordionTemplate, { ...jsonData.seoText.faq });
      this.#elements.seoText.querySelectorAll('.hedin-accordion').forEach(el => new HedinAccordion(el));
    }
    this.#elements.seoTextContainer.classList.toggle('hidden', !jsonData.seoText?.faq);

    // Mobile selected filters
    for(const [key, value] of Object.entries(jsonData.humanReadableFilters)) {
      const element = document.getElementById(`stores-mobile-filters--${key}`),
        elementValue = element.querySelector('.filter-value');

      element.classList.toggle('hidden', !value);
      elementValue.innerText = value;
    }

    this.#elements.mobileFiltersContent.classList.toggle('hidden', !Object.values(jsonData.humanReadableFilters).filter(Boolean).length);
  }

  /**
   * Resets all filters
   * @private
   */
  #resetFilters() {
    // Reset city checkboxes
    for (const cityCheckbox of this.#elements.cityCheckboxes) {
      cityCheckbox.checked = false;
    }

    // Reset dropdowns
    window.multiselects['stores-brands-dropdown'].unSelectAll();
    window.multiselects['stores-service-dropdown'].unSelectAll();
  }

  /**
   * Generates URL according to selected filters
   * @returns {string} query string
   */
  #generateUrl() {
    // Get checkbox values
    const cities = [ ...this.#elements.cityCheckboxes ].filter(c => c.checked).map(c => c.value);
    const citiesQueryString = cities.length ? `locations[]=${ cities.join('&locations[]=') }` : false;

    const brand = window.multiselects['stores-brands-dropdown'].getValue();
    const brandQueryString = brand.length ? `brand=${ brand[0] }` : false;

    const service = window.multiselects['stores-service-dropdown'].getValue();
    const serviceQueryString = service.length ? `department=${ service[0] }` : false;

    const queryStringParts = [ serviceQueryString, brandQueryString, citiesQueryString ].filter(Boolean);

    if (queryStringParts.length) {
      return `?${ queryStringParts.join('&') }`;
    }

    return window.location.pathname;
  }

  /**
   * Init method
   *
   * @static
   */
  static init() {
    const storePageContainer = document.querySelector('.stores-page');
    if(storePageContainer) {
      new StoreCollection(storePageContainer);
    }
  }
}

export default StoreCollection;
